import styles from './ChatRoom.module.css';
import React, { useEffect, useMemo, useRef, useState, useCallback, Suspense, lazy } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import WSClient from '../../../websocket/WebSocket';
import { fetchAiKeywords, fetchAiPlan, confirmAiPlan } from '../../../apis/aiApi';
import { getProfile } from '../../../apis/api';
import { getChatHistory, createInviteLink, addMember, getChatRoomMembers, deleteChatRoom, uploadChatImage, createPoll, getChatRoomNotices, createPollNotice, markRoomAsRead } from '../../../apis/chatApi';
import PollBubble from './Poll/PollBubble'; // Keep synchronous as it's used in list
import AiPlanBubble from './AiPlanBubble/AiPlanBubble'; // AI 여행 계획 카드
import sendIcon from "../../../assets/pic/send.svg";
import searchIcon from "../../../assets/pic/search.svg";
import menuIcon from "../../../assets/pic/menu.svg";
import backIcon from "../../../assets/pic/arrow2.svg";
import pic1 from "../../../assets/pic/pic1.png";
import pic2 from "../../../assets/pic/pic2.png";
import pic3 from "../../../assets/pic/pic3.png";
import pic4 from "../../../assets/pic/pic4.png";
import pic5 from "../../../assets/pic/pic5.png";
import pic6 from "../../../assets/pic/pic6.png";
import resolveMediaUrl from '../../../utils/resolveMediaUrl';
import useDataStore from '../../../stores/useDataStore';
import formatDistanceKm from '../../../utils/formatDistance';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Virtuoso } from 'react-virtuoso';
import EmoticonPicker, { EmoticonButton, EmoticonPanel } from '../../../components/Emoticon/EmoticonPicker';


const makeClientId = () => {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return `p_${crypto.randomUUID()}`;
        }
    } catch (_) {
        // ignore
    }
    return `p_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const ensurePlanPlaceIds = (plan) => {
    if (!plan || typeof plan !== 'object') return plan;
    const schedule = Array.isArray(plan?.schedule) ? plan.schedule : [];
    const nextSchedule = schedule.map((day) => {
        const places = Array.isArray(day?.places) ? day.places : [];
        return {
            ...day,
            places: places.map((p) => ({
                ...p,
                _clientId: p?._clientId || p?.placeId || p?.id || makeClientId(),
            })),
        };
    });

    return {
        ...plan,
        schedule: nextSchedule,
    };
};

const reorderList = (list, startIndex, endIndex) => {
    const result = Array.isArray(list) ? [...list] : [];
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

const UserSearchModal = lazy(() => import('../ChatList/UserSearchModal'));
const CreatePollModal = lazy(() => import('./Poll/CreatePollModal'));
const TripTimeline = lazy(() => import('./TripTimeline/TripTimeline'));

// Helper function to generate stable-ish unique keys for messages.
// NOTE: Some backends send timestamps with low resolution (e.g., seconds) and
// may omit an id for realtime events. If we key only by ts, React will reuse
// elements and new messages can appear to “not render”.
const keyOf = (m) => {
    const id = m?.id ?? m?.messageId;
    if (id !== undefined && id !== null && id !== '') return String(id);
    if (m?.clientTempId) return String(m.clientTempId);

    const room = m?.chatRoomId ?? '';
    const sender = m?.senderUserId ?? m?.senderName ?? '';
    const ts = m?.ts ?? '';
    const type = m?.messageType ?? '';
    const text = m?.message ?? '';
    return `${room}-${sender}-${ts}-${type}-${text}`;
};

// [Optimized] Helpers moved outside
const myGradient = "linear-gradient(135deg, #ffd77a, #ff9a3c)";

const getUserColor = (id, name) => {
    const key = id ? String(id) : name;
    if (!key) return "#ddd";

    let hash = 0;
    for (let i = 0; i < key.length; i++) {
        hash = key.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

const getDefaultProfileImage = (userId) => {
    if (!userId) return pic1;
    const images = [pic1, pic2, pic3, pic4, pic5, pic6];
    const index = userId % images.length;
    return images[index];
};

const ChatRoom = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [activeRoom, setActiveRoom] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [aiKeywords, setAiKeywords] = useState([]);
    const [aiPlan, setAiPlan] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiError, setAiError] = useState(null);
    const [aiInsertIndex, setAiInsertIndex] = useState(null);
    const [selectedKeywords, setSelectedKeywords] = useState([]);
    const [scrollParentEl, setScrollParentEl] = useState(null);

    // 공지(Notice)
    const [notices, setNotices] = useState([]);
    const [noticesLoading, setNoticesLoading] = useState(false);
    const [isNoticeOpen, setIsNoticeOpen] = useState(false);

    const setNoticesForRoom = useDataStore((s) => s.setNoticesForRoom);

    // [수정] 스크롤 제어
    const messageListRef = useRef(null);
    const virtuosoRef = useRef(null);
    const messagesRef = useRef([]);
    const fileInputRef = useRef(null); // [New] 파일 입력 참조
    const [showEmoticonPanel, setShowEmoticonPanel] = useState(false); // 이모티콘 패널 표시
    const hasScrolledToUnread = useRef(false);
    const initialUnreadCount = useRef(location.state?.unreadCount || 0);
    const isAtBottomRef = useRef(true);
    const incomingQueueRef = useRef([]);
    const flushRafRef = useRef(null);
    const messageIdSetRef = useRef(new Set());
    const clientTempIdSetRef = useRef(new Set());

    // Virtuoso item list ref (스크롤 하단 이동에 사용)
    const listItemsRef = useRef([]);

    const rebuildMessageIndex = useCallback((list) => {
        const nextIdSet = new Set();
        const nextTempSet = new Set();
        (Array.isArray(list) ? list : []).forEach((m) => {
            const incomingId = m?.id ?? m?.messageId;
            if (incomingId !== undefined && incomingId !== null && incomingId !== '') {
                nextIdSet.add(String(incomingId));
            }
            if (m?.clientTempId) {
                nextTempSet.add(String(m.clientTempId));
            }
        });
        messageIdSetRef.current = nextIdSet;
        clientTempIdSetRef.current = nextTempSet;
    }, []);

    const replaceMessages = useCallback((list) => {
        const next = Array.isArray(list) ? list : [];
        rebuildMessageIndex(next);
        setMessages(next);
    }, [rebuildMessageIndex]);

    const scrollToBottom = useCallback((behavior = 'auto') => {
        requestAnimationFrame(() => {
            const index = Math.max((listItemsRef.current?.length ?? 0) - 1, 0);
            virtuosoRef.current?.scrollToIndex?.({ index, align: 'end', behavior });
        });
    }, []);

    // Virtuoso item list (AI 블록 포함)
    const listItems = useMemo(() => {
        const items = [];
        const renderAi = aiKeywords.length > 0 && aiInsertIndex !== null;
        if (renderAi && aiInsertIndex === 0) {
            items.push({ type: 'ai', key: 'ai' });
        }

        messages.forEach((m, msgIdx) => {
            items.push({ type: 'msg', key: keyOf(m), m, msgIdx });
            if (renderAi && aiInsertIndex === msgIdx + 1) {
                items.push({ type: 'ai', key: 'ai' });
            }
        });

        if (renderAi && aiInsertIndex > messages.length) {
            items.push({ type: 'ai', key: 'ai' });
        }

        listItemsRef.current = items;
        return items;
    }, [messages, aiKeywords, aiInsertIndex]);

    // 내 정보 관리
    const [me, setMe] = useState(null);

    // [추가] 메뉴 및 모달 상태
    const [showMenu, setShowMenu] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showTimeline, setShowTimeline] = useState(false); // [New State]
    const [showCreatePoll, setShowCreatePoll] = useState(false); // [New State]
    const [profileModalUser, setProfileModalUser] = useState(null);
    const [showMembersModal, setShowMembersModal] = useState(false);
    const [memberList, setMemberList] = useState([]);

    useEffect(() => {
        const loadProfile = async () => {
            try {
                const profile = await getProfile();
                // 백엔드 User 엔티티의 id(Long)와 nickname/username 사용
                setMe({
                    userId: profile.id || profile.userId, // 백엔드 응답 필드 확인 필요. 보통 id
                    userName: profile.nickname || profile.username,
                    profileImageUrl: profile.profileImageUrl // [Fix] 프로필 이미지 추가
                });
            } catch (error) {
                console.error("Failed to load profile for chat", error);
                // 로그인 안된 경우 처리 (여기서는 임시로 랜덤)
                const newId = Math.floor(Math.random() * 100000);
                setMe({ userId: newId, userName: `Guest_${newId}` });
            }
        };
        loadProfile();
    }, []);

    const loadNotices = useCallback(async (roomId) => {
        if (roomId === undefined || roomId === null) return;

        const key = String(roomId);
        const cache = useDataStore.getState();
        const cached = cache.noticesByRoomId?.[key];
        const fetchedAt = cache.noticesFetchedAtByRoomId?.[key] || 0;
        // 공지는 자주 바뀌지 않으니 짧은 TTL 캐시로 체감 속도 개선
        if (Array.isArray(cached) && Date.now() - fetchedAt < 30000) {
            setNotices(cached);
            return;
        }

        setNoticesLoading(true);
        try {
            const data = await getChatRoomNotices(roomId);
            const arr = Array.isArray(data) ? data : [];
            setNotices(arr);
            setNoticesForRoom(roomId, arr);
        } catch (e) {
            console.error('Failed to load notices:', e);
            setNotices([]);
        } finally {
            setNoticesLoading(false);
        }
    }, [setNoticesForRoom]);

    useEffect(() => {
        const roomId = activeRoom?.roomId ?? activeRoom?.id;
        if (!roomId) return;
        loadNotices(roomId);
    }, [activeRoom, loadNotices]);

    useEffect(() => {
        hasScrolledToUnread.current = false;
        initialUnreadCount.current = location.state?.unreadCount || 0;
        incomingQueueRef.current = [];
        if (flushRafRef.current) {
            cancelAnimationFrame(flushRafRef.current);
            flushRafRef.current = null;
        }
        rebuildMessageIndex([]);
    }, [activeRoom, location.state?.unreadCount, rebuildMessageIndex]);

    const ws = useMemo(() => new WSClient({ reconnectDelay: 4000 }), []);



    const flushIncomingMessages = useCallback(() => {
        flushRafRef.current = null;
        const batch = incomingQueueRef.current;
        if (!batch.length) return;
        incomingQueueRef.current = [];

        setMessages((prev) => {
            let next = prev;
            let didChange = false;

            // 로컬 인덱스(Set) 기반 O(1) 중복 체크
            const idSet = messageIdSetRef.current;
            const tempSet = clientTempIdSetRef.current;

            for (const msg of batch) {
                const incomingId = msg?.id ?? msg?.messageId;
                if (incomingId !== undefined && incomingId !== null && incomingId !== '') {
                    const key = String(incomingId);
                    if (idSet.has(key)) continue;
                    if (!didChange) {
                        next = Array.isArray(prev) ? prev.slice() : [];
                        didChange = true;
                    }
                    idSet.add(key);
                    next.push(msg);
                    continue;
                }

                if (msg?.clientTempId) {
                    const key = String(msg.clientTempId);
                    if (tempSet.has(key)) continue;
                    if (!didChange) {
                        next = Array.isArray(prev) ? prev.slice() : [];
                        didChange = true;
                    }
                    tempSet.add(key);
                    next.push(msg);
                    continue;
                }

                // id/temporary id 둘 다 없는 경우는 중복 체크 불가 → 그대로 추가
                if (!didChange) {
                    next = Array.isArray(prev) ? prev.slice() : [];
                    didChange = true;
                }
                next.push(msg);
            }

            return didChange ? next : prev;
        });
    }, []);

    const handleReceive = useCallback((msg, roomId) => {
        // [Fix] 타입 불일치(String vs Number) 방지
        if (msg?.chatRoomId && String(msg.chatRoomId) !== String(roomId)) return;
        incomingQueueRef.current.push(msg);
        if (flushRafRef.current) return;
        flushRafRef.current = requestAnimationFrame(flushIncomingMessages);
    }, [flushIncomingMessages]);

    // [수정] 채팅방 진입 시 안 읽은 메시지 위치로 스크롤 (카카오톡처럼)
    // scrollParentEl과 virtuosoRef가 준비되었을 때만 스크롤 시도
    useEffect(() => {
        // virtuosoRef와 scrollParentEl이 준비되지 않았으면 아직 시도하지 않음
        if (!scrollParentEl || !virtuosoRef.current) {
            return;
        }

        // 이미 스크롤 완료됨
        if (hasScrolledToUnread.current) {
            // 새 메시지가 왔을 때 하단에 있으면 자동 스크롤
            if (isAtBottomRef.current) {
                scrollToBottom('smooth');
            }
            return;
        }

        // 메시지가 아직 로드 안 됨
        if (messages.length === 0) {
            return;
        }

        const unread = initialUnreadCount.current || 0;

        // Virtuoso가 완전히 마운트될 때까지 재시도하는 함수
        const attemptScroll = (retriesLeft = 5) => {
            if (!virtuosoRef.current?.scrollToIndex) {
                if (retriesLeft > 0) {
                    setTimeout(() => attemptScroll(retriesLeft - 1), 100);
                }
                return;
            }

            if (unread > 0 && unread < messages.length) {
                // 안 읽은 메시지가 있으면 첫 번째 안 읽은 메시지로 스크롤
                let targetIdx = messages.length - unread;
                targetIdx = Math.max(0, Math.min(targetIdx, messages.length - 1));
                const renderAi = aiKeywords.length > 0 && aiInsertIndex !== null;
                const offset = renderAi && aiInsertIndex <= targetIdx ? 1 : 0;
                const listIndex = targetIdx + offset;

                virtuosoRef.current.scrollToIndex({
                    index: listIndex,
                    align: 'start',
                    behavior: 'auto'
                });
            } else {
                // 다 읽었거나 unread가 없으면 맨 아래로
                const lastIndex = Math.max((listItemsRef.current?.length ?? 0) - 1, 0);
                virtuosoRef.current.scrollToIndex({
                    index: lastIndex,
                    align: 'end',
                    behavior: 'auto'
                });
            }

            hasScrolledToUnread.current = true;
        };

        // 약간의 딜레이 후 스크롤 시도 (Virtuoso 렌더링 완료 대기)
        const timeoutId = setTimeout(() => attemptScroll(), 150);
        return () => clearTimeout(timeoutId);
    }, [messages, scrollToBottom, aiKeywords, aiInsertIndex, scrollParentEl]);

    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    useEffect(() => {
        if (aiKeywords.length > 0) {
            scrollToBottom('smooth');
        }
    }, [aiKeywords, scrollToBottom]);

    useEffect(() => {
        let isMounted = true;
        const init = async () => {
            try {
                // 1. 네비게이션으로 전달된 방 ID 확인
                let roomId = location.state?.roomId;
                let roomName = location.state?.roomName || '채팅방';

                // roomId가 있으면 즉시 렌더링 시작 (Non-blocking)
                if (roomId) {
                    setActiveRoom({ roomId, name: roomName, ...location.state });
                    setIsLoading(false); // [Optimization] 즉시 로딩 해제

                    // 백그라운드 데이터 로딩
                    // 0) 읽음 처리 (비동기)
                    markRoomAsRead(roomId).catch(err => console.error("Mark read failed", err));

                    // 1) 채팅 내역
                    getChatHistory(roomId).then(history => {
                        if (isMounted && Array.isArray(history) && history.length) {
                            replaceMessages(history);
                        }
                    }).catch(console.error);

                    // 2) 소켓 연결
                    ws.roomId = roomId;
                    ws.connect({
                        onMessage: (msg) => {
                            if (isMounted) handleReceive(msg, roomId);
                        },
                    });

                    // 3) 멤버 수 조회
                    fetchMembers(roomId);

                    // 4) 방 정보 상세 업데이트 (옵션)
                    WSClient.fetchRooms().then(rooms => {
                        if (!isMounted) return;
                        const r = rooms.find(r => (r.roomId || r.id) === roomId);
                        if (r) setActiveRoom(r);
                    }).catch(console.error);

                } else {
                    // roomId가 없으면 목록을 가져와야 함 (Blocking)
                    const rooms = await WSClient.fetchRooms();
                    if (isMounted) {
                        if (Array.isArray(rooms) && rooms.length > 0) {
                            const room = rooms[0];
                            const firstRoomId = room.roomId || room.room_id || room.id;
                            setActiveRoom(room);
                            setIsLoading(false); // 로딩 해제

                            // 채팅 내역 및 소켓 연결
                            const history = await getChatHistory(firstRoomId);
                            if (Array.isArray(history) && history.length) {
                                replaceMessages(history);
                            }
                            ws.roomId = firstRoomId;
                            ws.connect({
                                onMessage: (msg) => {
                                    if (isMounted) handleReceive(msg, firstRoomId);
                                },
                            });
                            fetchMembers(firstRoomId);

                        } else {
                            console.log("참여 중인 채팅방이 없습니다.");
                            setIsLoading(false);
                        }
                    }
                }
            } catch (e) {
                console.error(e);
                if (isMounted) setIsLoading(false);
            }
        };
        init();
        return () => {
            isMounted = false;
            ws.disconnect();
        };
    }, [ws, handleReceive, location.state, replaceMessages]);

    const handleSend = () => {
        if (!text.trim() || !activeRoom || !me) return;
        const roomId = activeRoom.roomId ?? activeRoom.id;

        let payload = WSClient.buildTalkMessage({
            text,
            senderName: me.userName,
            chatRoomId: roomId,
        });
        payload.senderUserId = me.userId;
        payload.profileImageUrl = me.profileImageUrl; // 메시지에 프로필 이미지 포함

        ws.send(payload);
        setText("");

        // 메시지 보낸 후 자동으로 맨 아래로 스크롤
        isAtBottomRef.current = true;
        requestAnimationFrame(() => {
            scrollToBottom('smooth');
        });
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !activeRoom || !me) return;
        const roomId = activeRoom.roomId ?? activeRoom.id;

        try {
            // 1. 이미지 업로드 API 호출
            const { imageUrl } = await uploadChatImage(roomId, file);

            // 2. 소켓으로 이미지 메시지 전송
            const payload = WSClient.buildImageMessage({
                imageUrl,
                senderName: me.userName,
                chatRoomId: roomId
            });
            payload.senderUserId = me.userId;
            payload.profileImageUrl = me.profileImageUrl;

            ws.send(payload);

            // 이미지 보낸 후 자동으로 맨 아래로 스크롤
            isAtBottomRef.current = true;
            requestAnimationFrame(() => {
                scrollToBottom('smooth');
            });
        } catch (error) {
            console.error("Image upload failed:", error);
            alert("사진 전송에 실패했습니다.");
        } finally {
            // 입력 초기화 (같은 파일 다시 선택 가능하도록)
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // 이모티콘 전송 핸들러
    const handleEmoticonSelect = (emoticon) => {
        if (!activeRoom || !me) return;
        const roomId = activeRoom.roomId ?? activeRoom.id;

        // 이모티콘을 이미지 메시지로 전송 (전체 URL 사용)
        const emoticonUrl = emoticon.src.startsWith('http')
            ? emoticon.src
            : `${window.location.origin}${emoticon.src}`;

        const payload = WSClient.buildImageMessage({
            imageUrl: emoticonUrl,
            senderName: me.userName,
            chatRoomId: roomId
        });
        payload.senderUserId = me.userId;
        payload.profileImageUrl = me.profileImageUrl;

        ws.send(payload);
        setShowEmoticonPanel(false);

        // 이모티콘 보낸 후 자동으로 맨 아래로 스크롤
        isAtBottomRef.current = true;
        requestAnimationFrame(() => {
            scrollToBottom('smooth');
        });
    };

    const handleAiAnalyze = async () => {
        if (aiLoading || !activeRoom) return;
        const roomId = activeRoom?.roomId ?? activeRoom?.id ?? 1;
        setAiLoading(true);
        setAiError(null);
        setAiPlan(null);
        setAiInsertIndex(null);
        setAiKeywords([]);
        setSelectedKeywords([]);
        try {
            const result = await fetchAiKeywords(roomId);
            const keywords = Array.isArray(result?.keywords) ? result.keywords : [];
            const normalizedKeywords = keywords
                .map((kw) => (kw === null || kw === undefined ? "" : String(kw).trim()))
                .filter(Boolean);

            if (!normalizedKeywords.length) {
                throw new Error("AI가 키워드를 찾지 못했습니다.");
            }

            const insertIndex = messagesRef.current.length;
            setAiKeywords(normalizedKeywords);
            setSelectedKeywords(normalizedKeywords);
            setAiInsertIndex(insertIndex);
        } catch (e) {
            console.error(e);
            setAiError(e?.message || "AI 분석에 실패했습니다. 잠시 후 다시 시도해주세요.");
        } finally {
            setAiLoading(false);
        }
    };

    const handleGeneratePlan = async () => {
        const targets = selectedKeywords.filter(Boolean);
        if (!targets.length) {
            alert("분석할 키워드를 선택해주세요.");
            return;
        }
        if (!activeRoom) {
            alert("채팅방 정보를 찾을 수 없습니다.");
            return;
        }
        const roomId = activeRoom?.roomId ?? activeRoom?.id;
        if (roomId === undefined || roomId === null) {
            alert("채팅방 ID를 찾을 수 없습니다.");
            return;
        }
        setAiLoading(true);
        setAiError(null);
        setAiPlan(null);
        try {
            const planResult = await fetchAiPlan(roomId, targets);
            setAiPlan(ensurePlanPlaceIds(planResult));
        } catch (planError) {
            console.error(planError);
            setAiError(planError?.message || "여행 계획 생성에 실패했습니다.");
        } finally {
            setAiLoading(false);
        }
    };

    const handleAiPlanDragEnd = useCallback((result) => {
        const { destination, source } = result || {};
        if (!destination || !source) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        setAiPlan((prev) => {
            const safePrev = ensurePlanPlaceIds(prev);
            const schedule = Array.isArray(safePrev?.schedule) ? safePrev.schedule : [];
            const sourceDayIndex = Number(source.droppableId);
            const destDayIndex = Number(destination.droppableId);
            if (Number.isNaN(sourceDayIndex) || Number.isNaN(destDayIndex)) return safePrev;
            if (!schedule[sourceDayIndex] || !schedule[destDayIndex]) return safePrev;

            const nextSchedule = schedule.map((d) => ({
                ...d,
                places: Array.isArray(d?.places) ? [...d.places] : [],
            }));

            if (sourceDayIndex === destDayIndex) {
                nextSchedule[sourceDayIndex].places = reorderList(
                    nextSchedule[sourceDayIndex].places,
                    source.index,
                    destination.index
                );
            } else {
                const sourcePlaces = nextSchedule[sourceDayIndex].places;
                const destPlaces = nextSchedule[destDayIndex].places;
                const [moved] = sourcePlaces.splice(source.index, 1);
                destPlaces.splice(destination.index, 0, moved);
                nextSchedule[sourceDayIndex].places = sourcePlaces;
                nextSchedule[destDayIndex].places = destPlaces;
            }

            return {
                ...safePrev,
                schedule: nextSchedule,
            };
        });
    }, []);

    const handleToggleKeyword = (kw) => {
        setSelectedKeywords((prev) => {
            const exists = prev.includes(kw);
            if (exists) return prev.filter((k) => k !== kw);
            return [...prev, kw];
        });
    };

    const handleConfirmPlan = async () => {
        if (!aiPlan) return;
        try {
            const roomId = activeRoom?.roomId ?? activeRoom?.id;
            await confirmAiPlan({
                ...aiPlan,
                ...(roomId !== undefined && roomId !== null ? { chatRoomId: roomId } : {})
            });

            // 채팅방에 AI 여행 계획 생성 알림 메시지 전송 (카드 형태)
            if (roomId && me) {
                const payload = WSClient.buildAiPlanMessage({
                    senderName: me.userName,
                    chatRoomId: roomId,
                    planTitle: aiPlan?.title,
                    planData: aiPlan // 전체 계획 데이터 전달
                });
                payload.senderUserId = me.userId;
                payload.profileImageUrl = me.profileImageUrl;
                ws.send(payload);
            }

            alert("여행 계획이 저장되었습니다! 우측 상단 메뉴 > '일정 관리'에서 수정할 수 있습니다.");
        } catch (error) {
            console.error(error);
            alert("계획 저장에 실패했습니다.");
        }
    };

    const handleKeyDown = (e) => {
        if (e.isComposing || e.nativeEvent.isComposing) return;
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // [추가] 초기 멤버 수 상태 (기본 1명)
    const [memberCount, setMemberCount] = useState(1);
    const [memberProfiles, setMemberProfiles] = useState({}); // userId -> profileImageUrl 매핑

    // [추가] API import (파일 상단에 추가 필요하지만 여기서는 로직만)
    // import { getChatRoomMembers, deleteChatRoom } from '../../../apis/chatApi';

    const handleCreateInvite = async () => {
        if (!activeRoom) return;
        const roomId = activeRoom.roomId ?? activeRoom.id;
        try {
            const data = await createInviteLink(roomId);
            if (data && data.inviteUrl) {
                // 클립보드 복사
                await navigator.clipboard.writeText(data.inviteUrl);
                alert(`초대 링크가 복사되었습니다!\n${data.inviteUrl}`);
            } else {
                alert("초대 링크 생성에 실패했습니다.");
            }
        } catch (error) {
            console.error(error);
            alert("초대 링크 생성 중 오류가 발생했습니다.");
        }
        setShowMenu(false);
    };

    const handleAddMemberId = async (identifier) => {
        if (!activeRoom) return;
        const roomId = activeRoom.roomId ?? activeRoom.id;
        try {
            await addMember(roomId, identifier);
            alert(`${identifier}님이 채팅방에 추가되었습니다.`);
            // 멤버 수 갱신
            fetchMembers(roomId);
        } catch (error) {
            console.error(error);
            alert("멤버 추가에 실패했습니다. ID를 확인해주세요.");
        }
        setShowInviteModal(false);
        setShowMenu(false);
    };

    const fetchMembers = async (roomId) => {
        try {
            const data = await getChatRoomMembers(roomId);
            if (data && data.memberCount) {
                setMemberCount(data.memberCount);
            }
            if (data && Array.isArray(data.members)) {
                // [Debug] 멤버 데이터 확인
                console.log("ChatRoom Members Load:", data.members);
                const profiles = {};
                data.members.forEach(member => {
                    if (member.userId && member.profileImageUrl) {
                        profiles[member.userId] = member.profileImageUrl;
                    }
                });
                setMemberProfiles(prev => ({ ...prev, ...profiles }));
                setMemberList(data.members);
            } else {
                console.log("ChatRoom Members Data is empty or invalid:", data);
            }
        } catch (error) {
            console.error("Failed to fetch members:", error);
        }
    };

    const handleDeleteRoom = async () => {
        if (!activeRoom) return;
        const roomId = activeRoom.roomId ?? activeRoom.id;
        if (!window.confirm("정말로 채팅방을 삭제하시겠습니까? (방장만 가능합니다)")) return;

        try {
            await deleteChatRoom(roomId);
            alert("채팅방이 삭제되었습니다.");
            navigate('/home'); // 또는 채팅 목록으로 이동
        } catch (error) {
            console.error("Failed to delete room:", error);
            alert("채팅방 삭제에 실패했습니다. 방장 권한이 있는지 확인해주세요.");
        }
    };

    const handleCreatePoll = async (question, options) => {
        if (!activeRoom) return;
        const roomId = activeRoom.roomId ?? activeRoom.id;
        try {
            const created = await createPoll(roomId, question, options);
            // 성공 시 소켓으로 메시지가 오므로 별도 처리 불필요
            setShowCreatePoll(false);
            // 방금 만든 투표를 기본 공지로 등록할지 여부 안내
            if (created?.pollId) {
                const shouldNotice = window.confirm('투표가 생성되었습니다. 공지로 등록할까요?');
                if (shouldNotice) {
                    await createPollNotice(roomId, created.pollId, created.question || '새 투표가 생성되었습니다.');
                    await loadNotices(roomId);
                }
            }
        } catch (error) {
            console.error(error);
            alert("투표 생성에 실패했습니다.");
        }
    };

    const handleRegisterPollNotice = useCallback(async ({ pollId, poll }) => {
        const roomId = activeRoom?.roomId ?? activeRoom?.id;
        if (!roomId || !pollId) return;

        const defaultMsg = poll?.question ? `투표 참여 부탁! (${poll.question})` : '투표 참여 부탁!';
        const message = window.prompt('공지 메시지를 입력하세요', defaultMsg);
        if (message === null) return; // cancelled
        if (!String(message).trim()) return;

        try {
            await createPollNotice(roomId, pollId, String(message).trim());
            await loadNotices(roomId);
            alert('공지로 등록되었습니다.');
        } catch (e) {
            console.error(e);
            alert('공지 등록에 실패했습니다.');
        }
    }, [activeRoom, loadNotices]);

    const handleOpenProfile = useCallback((userInfo) => {
        if (!userInfo) return;
        setProfileModalUser(userInfo);
    }, []);

    const handleCloseProfile = () => setProfileModalUser(null);

    const renderAiBlock = () => {
        const schedule = Array.isArray(aiPlan?.schedule) ? aiPlan.schedule : [];

        return (
            <div className={styles.aiKeywordCard}>
                <p className={styles.aiKeywordTitle}>
                    AI 분석을 통해 현재 채팅방의 여행 핵심 키워드를 정리해드려요!
                </p>
                <div className={styles.aiKeywordList}>
                    {aiKeywords.map((kw, idx) => {
                        const active = selectedKeywords.includes(kw);
                        return (
                            <button
                                type="button"
                                key={`${kw}-${idx}`}
                                className={`${styles.aiKeywordChip} ${styles.aiKeywordChipSelectable} ${active ? styles.aiKeywordChipSelected : ''}`}
                                onClick={() => handleToggleKeyword(kw)}
                            >
                                {kw}
                            </button>
                        );
                    })}
                </div>
                <div className={styles.aiActionRow}>
                    <button
                        className={styles.aiGenerateButton}
                        onClick={handleGeneratePlan}
                        disabled={!selectedKeywords.length || aiLoading}
                    >
                        {aiLoading ? "분석 중..." : "선택한 키워드로 분석"}
                    </button>
                    {aiError && <span className={styles.aiError}>{aiError}</span>}
                </div>

                <div className={styles.aiPlanSection}>
                    <div className={styles.aiPlanHeader}>
                        <div className={styles.aiPlanHeadingText}>
                            <p className={styles.aiPlanLabel}>여행 계획</p>
                            {aiPlan?.title && <h4 className={styles.aiPlanTitle}>{aiPlan.title}</h4>}
                        </div>
                        {aiLoading && !aiPlan && <span className={styles.aiPlanBadge}>생성 중...</span>}
                    </div>
                    {!aiPlan && !aiLoading && (
                        <p className={styles.aiPlanEmpty}>키워드를 선택하고 "선택한 키워드로 분석"을 눌러주세요.</p>
                    )}
                    {aiPlan?.description && (
                        <p className={styles.aiPlanDescription}>{aiPlan.description}</p>
                    )}

                    {schedule.length > 0 ? (
                        <DragDropContext onDragEnd={handleAiPlanDragEnd}>
                            <div className={styles.aiPlanDays}>
                                {schedule.map((dayPlan, idx) => {
                                    const places = Array.isArray(dayPlan?.places) ? dayPlan.places : [];
                                    const dayLabel =
                                        dayPlan?.day === 0 || dayPlan?.day
                                            ? `Day ${dayPlan.day}`
                                            : `Day ${idx + 1}`;

                                    return (
                                        <div key={`day-${idx}`} className={styles.aiPlanDay}>
                                            <div className={styles.aiPlanDayTitle}>{dayLabel}</div>
                                            <Droppable droppableId={String(idx)}>
                                                {(provided) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.droppableProps}
                                                        className={styles.aiPlanPlaces}
                                                    >
                                                        {places.map((place, pIdx) => {
                                                            const draggableId = String(place?._clientId ?? `${idx}-${pIdx}`);
                                                            const distanceLabel = formatDistanceKm(place?.distanceToNext);
                                                            const distanceText = distanceLabel ? `다음까지 ${distanceLabel}` : '';
                                                            return (
                                                                <Draggable
                                                                    key={draggableId}
                                                                    draggableId={draggableId}
                                                                    index={pIdx}
                                                                >
                                                                    {(dragProvided, dragSnapshot) => (
                                                                        <div
                                                                            ref={dragProvided.innerRef}
                                                                            {...dragProvided.draggableProps}
                                                                            {...dragProvided.dragHandleProps}
                                                                            className={`${styles.aiPlanPlace} ${dragSnapshot.isDragging ? styles.aiPlanPlaceDragging : ''}`}
                                                                        >
                                                                            <div className={styles.aiPlanPlaceInfo}>
                                                                                <div className={styles.aiPlanPlaceName}>{place?.name || "추천 장소"}</div>
                                                                                <div className={styles.aiPlanPlaceMeta}>
                                                                                    {place?.category && <span>{place.category}</span>}
                                                                                    {place?.address && <span>{place.address}</span>}
                                                                                </div>
                                                                                {distanceText && (
                                                                                    <div className={styles.aiPlanDistance}>{distanceText}</div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </Draggable>
                                                            );
                                                        })}
                                                        {places.length === 0 && (
                                                            <div className={styles.aiPlanEmpty}>추천 장소 정보가 없습니다.</div>
                                                        )}
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </Droppable>
                                        </div>
                                    );
                                })}
                            </div>
                        </DragDropContext>
                    ) : (
                        <p className={styles.aiPlanEmpty}>
                            {aiLoading ? "여행 계획을 구성하고 있습니다..." : "생성된 여행 계획이 없습니다."}
                        </p>
                    )}

                    {aiPlan && (
                        <button
                            onClick={handleConfirmPlan}
                            style={{
                                marginTop: '15px',
                                padding: '12px',
                                backgroundColor: '#FF5C00',
                                color: 'white',
                                border: 'none',
                                borderRadius: '12px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                width: '100%'
                            }}
                        >
                            이 일정으로 여행 시작하기
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const shouldRenderAi = aiKeywords.length > 0 && aiInsertIndex !== null;
    const aiBlockElement = shouldRenderAi ? renderAiBlock() : null;

    if (isLoading) return <div className={styles.loadingScreen}>채팅방에 입장 중입니다...</div>;



    return (
        <div className={styles.screen}>
            <div className={styles.topBarWrapper}>
                <header className={styles.topBar}>
                    <button className={styles.navBtn} onClick={() => navigate(-1)} aria-label="back">
                        <img src={backIcon} alt="back" />
                    </button>
                    <div className={styles.titleGroup}>
                        <h1 className={styles.title}>{activeRoom?.name || "채팅방"}</h1>
                        <span
                            className={styles.memberCount}
                            role="button"
                            tabIndex={0}
                            onClick={() => setShowMembersModal(true)}
                            onKeyDown={(e) => { if (e.key === 'Enter') setShowMembersModal(true); }}
                        >
                            {memberCount}
                        </span>
                    </div>
                    <div className={styles.topIcons}>
                        <img
                            className={styles.iconImg}
                            src={searchIcon}
                            alt="search"
                            onClick={() => navigate('/chat/search')}
                            style={{ cursor: 'pointer' }}
                        />
                        <div style={{ position: 'relative' }}>
                            <img
                                className={styles.iconImg}
                                src={menuIcon}
                                alt="menu"
                                onClick={() => setShowMenu(!showMenu)}
                                style={{ cursor: 'pointer' }}
                            />
                            {showMenu && (
                                <div style={{
                                    position: 'absolute',
                                    top: '30px',
                                    right: '0',
                                    backgroundColor: 'white',
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    zIndex: 100,
                                    width: '150px',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}>
                                    <button
                                        onClick={() => {
                                            setShowMenu(false);
                                            setShowTimeline(true);
                                        }}
                                        style={{ padding: '10px', borderBottom: '1px solid #eee', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontWeight: 'bold', color: '#FF5C00' }}
                                    >
                                        일정 관리 (NEW)
                                    </button>
                                    <button
                                        onClick={handleCreateInvite}
                                        style={{ padding: '10px', borderBottom: '1px solid #eee', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer' }}
                                    >
                                        초대 링크 복사
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowMenu(false);
                                            setShowInviteModal(true);
                                        }}
                                        style={{ padding: '10px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                                    >
                                        멤버 추가
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowMenu(false);
                                            setShowCreatePoll(true);
                                        }}
                                        style={{ padding: '10px', borderBottom: '1px solid #eee', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', color: '#28C2CE', fontWeight: 'bold' }}
                                    >
                                        투표 만들기
                                    </button>
                                    <button
                                        onClick={handleDeleteRoom}
                                        style={{ padding: '10px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', color: 'red' }}
                                    >
                                        나가기(방삭제)
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>
            </div>

            <div
                className={styles.messageList}
                ref={(el) => {
                    messageListRef.current = el;
                    if (el && scrollParentEl !== el) setScrollParentEl(el);
                }}
            >
                <div className={styles.listHeader}>
                    {(() => {
                        const pollNotices = (Array.isArray(notices) ? notices : []).filter((n) => (n?.type ? n.type === 'POLL' : true) && n?.pollId);
                        const hasNotices = pollNotices.length > 0;
                        if (!hasNotices && !noticesLoading) return null;
                        return (
                            <div className={`${styles.noticePanel} ${isNoticeOpen ? styles.noticeOpen : ''}`} aria-label="공지">
                                <button
                                    type="button"
                                    className={styles.noticeBar}
                                    onClick={() => setIsNoticeOpen((prev) => !prev)}
                                >
                                    <div className={styles.noticeHeader}>
                                        <span className={styles.noticeDot} aria-hidden="true" />
                                        <span className={styles.noticeTitle}>공지</span>
                                        {hasNotices && <span className={styles.noticeCount}>{pollNotices.length}</span>}
                                        {noticesLoading && <span className={styles.noticeLoading}>업데이트 중...</span>}
                                    </div>
                                    <span className={styles.noticeChevron} aria-hidden="true" />
                                </button>
                                <div className={`${styles.noticeBody} ${isNoticeOpen ? styles.noticeBodyOpen : ''}`}>
                                    {hasNotices ? (
                                        <div className={styles.noticeList}>
                                            {pollNotices.map((n) => (
                                                <div key={n.noticeId ?? `${n.pollId}-${n.createdAt ?? ''}`} className={styles.noticeItem}>
                                                    {n.message && <div className={styles.noticeMessage}>{n.message}</div>}
                                                    <PollBubble
                                                        pollId={n.pollId}
                                                        className={styles.noticePollBubble}
                                                        currentUserId={me?.userId}
                                                        currentUserName={me?.userName}
                                                        compactWhenClosed
                                                        fallbackCreatorId={n.userId || n.creatorId}
                                                        fallbackCreatorName={n.userName || n.creatorName}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className={styles.noticeEmpty}>불러오는 중...</div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}
                    <div className={styles.aiToolbar}>
                        <button
                            className={styles.aiButton}
                            onClick={handleAiAnalyze}
                            disabled={aiLoading || !activeRoom}
                            aria-label="AI 키워드 분석"
                        >
                            {aiLoading ? "분석 중..." : "AI 키워드 분석"}
                        </button>
                    </div>
                </div>
                {scrollParentEl && (
                    <Virtuoso
                        ref={virtuosoRef}
                        data={listItems}
                        customScrollParent={scrollParentEl}
                        atBottomStateChange={(atBottom) => {
                            isAtBottomRef.current = atBottom;
                        }}
                        followOutput={(isAtBottom) => (isAtBottom ? 'smooth' : false)}
                        itemContent={(index, item) => {
                            if (item?.type === 'ai') {
                                return <div className={styles.virtuosoItem}>{aiBlockElement}</div>;
                            }
                            return (
                                <div className={styles.virtuosoItem}>
                                    <MessageRow
                                        m={item.m}
                                        me={me}
                                        memberProfiles={memberProfiles}
                                        onRegisterPollNotice={handleRegisterPollNotice}
                                        onProfileClick={handleOpenProfile}
                                        onViewTimeline={() => setShowTimeline(true)}
                                    />
                                </div>
                            );
                        }}
                    />
                )}
            </div>

            <div className={styles.inputBar}>
                {/* [New] 이미지 업로드 버튼 */}
                <button
                    className={styles.uploadBtn}
                    onClick={() => fileInputRef.current?.click()}
                    aria-label="upload image"
                >
                    <span>+</span>
                </button>
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    style={{ display: 'none' }}
                />
                {/* 입력창 + 이모티콘 버튼 wrapper */}
                <div className={styles.inputWrapper}>
                    <input
                        className={styles.input}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="메세지를 입력하세요"
                        onFocus={() => setShowEmoticonPanel(false)}
                    />
                    {/* 입력창 우측 끝 이모티콘 버튼 */}
                    <EmoticonButton onClick={() => setShowEmoticonPanel(prev => !prev)} />
                </div>
                <button className={styles.sendBtn} onClick={handleSend} disabled={!text.trim()} aria-label="send">
                    <img src={sendIcon} alt="" />
                </button>
            </div>

            {/* 이모티콘 패널 - 채팅방 화면 폭에 맞게 */}
            <EmoticonPanel
                isOpen={showEmoticonPanel}
                onClose={() => setShowEmoticonPanel(false)}
                onSelect={handleEmoticonSelect}
            />
            {/* [추가] 멤버 추가 모달 */}
            {/* Lazy Load Modals */}
            <Suspense fallback={null}>
                {showInviteModal && (
                    <UserSearchModal
                        onClose={() => setShowInviteModal(false)}
                        onConfirm={handleAddMemberId}
                    />
                )}
                {showTimeline && (
                    <TripTimeline
                        onClose={() => setShowTimeline(false)}
                        chatRoomId={activeRoom?.roomId}
                    />
                )}
                {showCreatePoll && (
                    <CreatePollModal
                        onClose={() => setShowCreatePoll(false)}
                        onCreate={handleCreatePoll}
                    />
                )}
            </Suspense>

            {profileModalUser && (
                <div className={styles.profileModalBackdrop} onClick={handleCloseProfile}>
                    <div
                        className={styles.profileModal}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={profileModalUser.profileImageUrl || getDefaultProfileImage(profileModalUser.userId)}
                            alt={profileModalUser.userName || profileModalUser.name}
                            className={styles.profileModalAvatar}
                        />
                        <div className={styles.profileModalName}>
                            {profileModalUser.userName || profileModalUser.name || '사용자'}
                        </div>
                        {profileModalUser.userId && (
                            <div className={styles.profileModalMeta}>ID: {profileModalUser.userId}</div>
                        )}
                        <button className={styles.profileModalClose} onClick={handleCloseProfile}>
                            닫기
                        </button>
                    </div>
                </div>
            )}

            {showMembersModal && (
                <div className={styles.profileModalBackdrop} onClick={() => setShowMembersModal(false)}>
                    <div
                        className={styles.memberModal}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className={styles.memberModalTitle}>참여자 ({memberCount})</h3>
                        <div className={styles.memberList}>
                            {memberList.map((m) => {
                                const displayName =
                                    m.nickname ||
                                    m.userName ||
                                    m.username ||
                                    m.name ||
                                    m.displayName ||
                                    `사용자 ${m.userId ?? m.id ?? ''}`;
                                const handle = m.username || m.userName;
                                return (
                                    <div key={m.userId ?? m.id} className={styles.memberItem}>
                                        <img
                                            src={m.profileImageUrl || getDefaultProfileImage(m.userId)}
                                            alt={displayName}
                                            className={styles.memberAvatar}
                                        />
                                        <div className={styles.memberInfo}>
                                            <div className={styles.memberName}>{displayName}</div>
                                            {handle && <div className={styles.memberMeta}>@{handle}</div>}
                                        </div>
                                    </div>
                                );
                            })}
                            {memberList.length === 0 && (
                                <div className={styles.memberEmpty}>멤버 정보를 불러올 수 없습니다.</div>
                            )}
                        </div>
                        <button className={styles.profileModalClose} onClick={() => setShowMembersModal(false)}>
                            닫기
                        </button>
                    </div>
                </div>
            )}
        </div >
    );
};

/* MessageRow Component for Memoization */
const MessageRow = React.memo(({ m, me, memberProfiles, onRegisterPollNotice, onProfileClick, onViewTimeline }) => {
    const isMe = me && ((m.senderUserId === me.userId) || (!m.senderUserId && m.senderName === me.userName));
    const avatarImg = m.senderName === "민수" ? pic2 : null;
    const avatarStyle = isMe
        ? { background: myGradient }
        : { background: getUserColor(m.senderUserId, m.senderName) };

    const myProfileImage = isMe ? me?.profileImageUrl : null;

    // ::AI_PLAN:: prefix 감지 및 파싱
    const isAiPlanMessage = typeof m.message === 'string' && m.message.startsWith('::AI_PLAN::');
    let parsedPlanData = null;
    if (isAiPlanMessage) {
        try {
            const jsonStr = m.message.replace('::AI_PLAN::', '');
            parsedPlanData = JSON.parse(jsonStr);
        } catch (e) {
            console.error('Failed to parse AI_PLAN message:', e);
        }
    }

    return (
        <div className={`${styles.messageRow} ${isMe ? styles.myRow : styles.otherRow}`}>
            {!isMe && (
                <>
                    <img
                        className={styles.avatar}
                        src={avatarImg || m.profileImageUrl || memberProfiles[m.senderUserId] || getDefaultProfileImage(m.senderUserId)}
                        alt={m.senderName}
                        onClick={() => onProfileClick && onProfileClick({
                            userId: m.senderUserId,
                            userName: m.senderName,
                            profileImageUrl: avatarImg || m.profileImageUrl || memberProfiles[m.senderUserId]
                        })}
                        style={{ cursor: onProfileClick ? 'pointer' : 'default' }}
                    />
                    <div className={styles.bubbleWrap}>
                        <div className={styles.senderName}>{m.senderName}</div>
                        {m.messageType === 'IMAGE' ? (
                            <div className={styles.imageBubble}>
                                <img
                                    src={resolveMediaUrl(m.imageUrl) || undefined}
                                    alt="사진"
                                    className={styles.chatImage}
                                    onError={() => {
                                        console.warn('[chat image load failed]', {
                                            imageUrl: m.imageUrl,
                                            resolved: resolveMediaUrl(m.imageUrl),
                                        });
                                    }}
                                />
                            </div>
                        ) : m.messageType === 'POLL' ? (
                            <PollBubble
                                pollId={m.pollId}
                                onRegisterNotice={onRegisterPollNotice}
                                currentUserId={me?.userId}
                                currentUserName={me?.userName}
                                fallbackCreatorId={m.senderUserId}
                                fallbackCreatorName={m.senderName}
                            />
                        ) : isAiPlanMessage && parsedPlanData ? (
                            <AiPlanBubble
                                planData={parsedPlanData}
                                senderName={m.senderName}
                                onViewDetail={onViewTimeline}
                            />
                        ) : (
                            <div className={`${styles.bubble} ${styles.otherBubble}`}>
                                {m.message}
                            </div>
                        )}
                    </div>
                </>
            )}

            {isMe && (
                <>
                    <div className={styles.bubbleWrap}>
                        <div
                            className={styles.senderName}
                            style={{ textAlign: 'right', marginRight: '4px' }}
                        >
                            me
                        </div>
                        {m.messageType === 'IMAGE' ? (
                            <div className={styles.imageBubbleMy}>
                                <img
                                    src={resolveMediaUrl(m.imageUrl)}
                                    alt="사진"
                                    className={styles.chatImage}
                                    onError={() => {
                                        console.warn('[chat image load failed]', {
                                            imageUrl: m.imageUrl,
                                            resolved: resolveMediaUrl(m.imageUrl),
                                        });
                                    }}
                                />
                            </div>
                        ) : m.messageType === 'POLL' ? (
                            <PollBubble
                                pollId={m.pollId}
                                onRegisterNotice={onRegisterPollNotice}
                                currentUserId={me?.userId}
                                currentUserName={me?.userName}
                                fallbackCreatorId={m.senderUserId}
                                fallbackCreatorName={m.senderName}
                            />
                        ) : isAiPlanMessage && parsedPlanData ? (
                            <AiPlanBubble
                                planData={parsedPlanData}
                                senderName={m.senderName}
                                onViewDetail={onViewTimeline}
                            />
                        ) : (
                            <div className={`${styles.bubble} ${styles.myBubble}`}>
                                {m.message}
                            </div>
                        )}
                    </div>
                    {myProfileImage ? (
                        <img className={styles.avatar} src={myProfileImage} alt={m.senderName} />
                    ) : (
                        avatarImg ? (
                            <img className={styles.avatar} src={avatarImg} alt={m.senderName} />
                        ) : (
                            <div
                                className={styles.unknownAvatar}
                                style={avatarStyle}
                                aria-label="me"
                            />
                        )
                    )}
                </>
            )}
        </div>
    );
});

export default ChatRoom;
